import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR, formatTimeID } from "@/lib/format";
import { toast } from "sonner";
import {
  Search, ShoppingCart, Plus, Minus, Trash2, LogOut, X, Store, Printer,
  Wallet, Smartphone, CreditCard, User, LockKeyhole, BarChart3, Ban, RotateCcw,
  Landmark, ArrowDownCircle, ArrowUpCircle, Repeat, ScanLine, KeyRound, Delete,
  LayoutDashboard, Users, ChevronDown, Clock, Home, Save, FolderOpen,
} from "lucide-react";

const ADMIN_LIKE = ["owner", "hr", "supervisor", "gudang", "logistik"];
const CATS = [
  { id: "all", name: "Semua" }, { id: "dress", name: "Dress" }, { id: "outer", name: "Outer" },
  { id: "rok", name: "Rok" }, { id: "setelan", name: "Setelan" }, { id: "hijab", name: "Hijab" },
];
const PMETA = [
  { id: "cash", icon: Wallet, label: "Tunai" },
  { id: "qris", icon: Smartphone, label: "QRIS" },
  { id: "transfer", icon: Landmark, label: "Transfer" },
  { id: "card", icon: CreditCard, label: "Debit" },
];
const KATEGORI_OUT = ["Operasional", "Parkir", "Perlengkapan", "Konsumsi", "Lainnya"];

const inp = "w-full h-11 px-3 rounded-lg bg-white border border-rose-200 text-sm text-slate-800 focus:outline-none focus:border-rose-400";
const initials = (n) => (n || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const loadLS = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

// ---------------------------------------------------------------------------
// PIN pad besar (layar kunci & modal sensitif)
// ---------------------------------------------------------------------------
function PinPad({ value, onChange, onSubmit, busy, testid = "pinpad" }) {
  const press = (d) => {
    if (busy) return;
    if (d === "C") return onChange("");
    if (d === "del") return onChange(value.slice(0, -1));
    if (value.length >= 6) return;
    const nv = value + d;
    onChange(nv);
    if (nv.length === 6 && onSubmit) setTimeout(() => onSubmit(nv), 150);
  };
  // PIN juga bisa diketik langsung via keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("del");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  return (
    <div data-testid={testid} className="select-none">
      <div className="flex justify-center gap-2.5 mb-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`w-4 h-4 rounded-full border-2 transition-colors ${i < value.length ? "bg-rose-600 border-rose-600" : "border-rose-200 bg-white"}`} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "del"].map((d) => (
          <button key={d} type="button" data-testid={`${testid}-key-${d}`} onClick={() => press(d)}
            className="h-14 rounded-xl bg-rose-50/60 hover:bg-rose-100 border border-rose-100 text-xl font-semibold text-slate-800 active:scale-95 transition flex items-center justify-center">
            {d === "del" ? <Delete className="w-5 h-5" /> : d}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layar terkunci / login petugas (Scan ID Card atau PIN)
// ---------------------------------------------------------------------------
function LockScreen({ title, desc, onUnlock, testid = "pos-lock-screen" }) {
  const [tab, setTab] = useState("pin");
  const [pin, setPin] = useState("");
  const [card, setCard] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const doAuth = async (payload) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/pos/attendant/auth", payload);
      toast.success(`Selamat bekerja, ${data.attendant.name}`, { duration: 1500 });
      onUnlock(data.attendant, data.session_id);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Autentikasi gagal";
      setErr(msg);
      setPin(""); setCard("");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#FBF7F4]/95 backdrop-blur flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-white rounded-2xl border border-rose-100 shadow-xl w-full max-w-md p-6">
        <div className="text-center mb-5">
          <div className="w-14 h-14 rounded-2xl bg-rose-600 mx-auto flex items-center justify-center mb-3"><LockKeyhole className="w-6 h-6 text-white" /></div>
          <div className="font-display font-bold text-xl text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 mt-1">{desc}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button data-testid="lock-tab-card" onClick={() => { setTab("card"); setErr(""); }}
            className={`h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${tab === "card" ? "bg-rose-600 text-white" : "bg-rose-50 text-rose-700 hover:bg-rose-100"}`}>
            <ScanLine className="w-4 h-4" /> Scan ID Card
          </button>
          <button data-testid="lock-tab-pin" onClick={() => { setTab("pin"); setErr(""); }}
            className={`h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${tab === "pin" ? "bg-rose-600 text-white" : "bg-rose-50 text-rose-700 hover:bg-rose-100"}`}>
            <KeyRound className="w-4 h-4" /> Masukkan PIN
          </button>
        </div>

        {tab === "card" && (
          <div>
            <input
              data-testid="lock-card-input" autoFocus value={card}
              onChange={(e) => setCard(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && card.trim() && !busy) doAuth({ card_code: card.trim() }); }}
              placeholder="Scan / ketik kode kartu lalu Enter (mis. KYN-XXXXXX)"
              className={inp + " text-center tracking-widest font-mono"}
            />
            <button data-testid="lock-card-submit" disabled={busy || !card.trim()} onClick={() => doAuth({ card_code: card.trim() })}
              className="mt-3 w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
              {busy ? "Memverifikasi..." : "Verifikasi Kartu"}
            </button>
          </div>
        )}

        {tab === "pin" && (
          <div>
            <div className="text-center text-xs text-slate-400 mb-3">Masukkan PIN 6 digit — sistem otomatis mengenali karyawan</div>
            <PinPad value={pin} onChange={setPin} busy={busy} testid="lock-pinpad"
              onSubmit={(v) => doAuth({ pin: v })} />
            <div className="text-center text-[10px] text-slate-400 mt-3">Bisa diketik langsung via keyboard</div>
          </div>
        )}
        {err && <div data-testid="lock-error" className="mt-4 text-center text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal dasar
// ---------------------------------------------------------------------------
function Modal({ title, sub, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 bg-slate-900/40 z-[90] flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-lg" : "max-w-md"} p-5 max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="font-display font-bold text-slate-900">{title}</div>
            {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
          </div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

// PIN kecil untuk verifikasi aksi sensitif di dalam modal
function PinField({ value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-slate-500">PIN Anda (verifikasi ulang)</label>
      <input data-testid={testid} type="password" inputMode="numeric" maxLength={6} value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="••••••" className={inp + " tracking-[0.5em] font-mono text-center"} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Buka Kasir — wajib autentikasi penanggung jawab (PIN / ID Card)
// ---------------------------------------------------------------------------
function BukaKasirModal({ lastKeep, onDone }) {
  const [pin, setPin] = useState("");
  const [card, setCard] = useState("");
  const [mode, setMode] = useState("pin");
  const [cash, setCash] = useState(lastKeep ? String(lastKeep) : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const now = new Date();

  const submit = async () => {
    const starting = parseInt(cash || 0);
    if (Number.isNaN(starting) || starting < 0) return toast.error("Saldo awal tidak valid");
    if (mode === "pin" && pin.length !== 6) return toast.error("Isi PIN 6 digit penanggung jawab");
    if (mode === "card" && !card.trim()) return toast.error("Scan / ketik kode kartu");
    setBusy(true);
    try {
      const payload = { starting_cash: starting, note };
      if (mode === "pin") { payload.pin = pin; } else { payload.card_code = card.trim(); }
      const { data } = await api.post("/pos/shifts/open", payload);
      toast.success(`Kasir dibuka • ${data.code} • Penanggung jawab: ${data.responsible_name}`);
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal buka kasir");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Buka Kasir" sub="Mulai sesi cash drawer dan catat modal uang kembalian." onClose={() => {}} testid="pos-open-shift-modal" wide>
      <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
        <div className="bg-rose-50/60 rounded-lg px-3 py-2"><div className="text-slate-400">Tanggal Buka</div><div className="font-semibold text-slate-800">{now.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}</div></div>
        <div className="bg-rose-50/60 rounded-lg px-3 py-2"><div className="text-slate-400">Jam Buka</div><div className="font-semibold text-slate-800">{now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} WIB</div></div>
      </div>
      <label className="text-xs text-slate-500">Penanggung Jawab</label>
      <div className="text-[11px] text-slate-400 bg-rose-50/60 rounded-lg px-3 py-2 mb-2 mt-1">Terverifikasi otomatis dari PIN / ID Card — setiap karyawan memiliki PIN unik, tidak perlu memilih nama.</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button data-testid="open-mode-pin" onClick={() => setMode("pin")} className={`h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${mode === "pin" ? "bg-rose-600 text-white" : "bg-rose-50 text-rose-700"}`}><KeyRound className="w-3.5 h-3.5" /> PIN</button>
        <button data-testid="open-mode-card" onClick={() => setMode("card")} className={`h-9 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 ${mode === "card" ? "bg-rose-600 text-white" : "bg-rose-50 text-rose-700"}`}><ScanLine className="w-3.5 h-3.5" /> ID Card</button>
      </div>
      {mode === "pin" ? (
        <div className="mb-3">
          <label className="text-xs text-slate-500">PIN Penanggung Jawab (6 digit)</label>
          <input data-testid="open-pin" type="password" inputMode="numeric" maxLength={6} value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="••••••" className={inp + " tracking-[0.5em] font-mono text-center"} />
        </div>
      ) : (
        <div className="mb-3">
          <label className="text-xs text-slate-500">Kode ID Card</label>
          <input data-testid="open-card" value={card} onChange={(e) => setCard(e.target.value.toUpperCase())} placeholder="KYN-XXXXXX" className={inp + " font-mono tracking-widest text-center"} />
        </div>
      )}
      <label className="text-xs text-slate-500">Saldo Awal / Modal Kembalian</label>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm text-slate-400">Rp</span>
        <input data-testid="pos-starting-cash" type="number" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="500.000" className={inp} />
      </div>
      {lastKeep > 0 && <div className="text-[11px] text-emerald-600 bg-emerald-50 rounded-lg px-2.5 py-1.5 mb-1">Sisa kembalian sesi sebelumnya {formatIDR(lastKeep)} otomatis terisi (bisa diubah)</div>}
      <div className="text-[11px] text-amber-600 bg-amber-50 rounded-lg px-2.5 py-1.5 mb-3">Pastikan nominal sesuai uang fisik di laci kasir. Saldo awal hanya bisa dikoreksi lewat penyesuaian berapproval owner.</div>
      <label className="text-xs text-slate-500">Catatan (opsional)</label>
      <textarea data-testid="open-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Contoh: Serah terima dari shift sebelumnya" className={inp + " h-16 py-2 resize-none mb-3"} />
      <div className="flex items-center justify-between text-sm mb-3"><span className="text-slate-500">Saldo awal tercatat</span><span className="font-bold text-rose-700">{formatIDR(parseInt(cash || 0) || 0)}</span></div>
      <button data-testid="pos-open-shift-btn" onClick={submit} disabled={busy} className="w-full h-12 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
        {busy ? "Memproses..." : "Buka Kasir & Mulai Shift"}
      </button>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal Uang Masuk / Pengeluaran / Setor Uang
// ---------------------------------------------------------------------------
function KasirMutModal({ kind, attendant, onClose, onDone }) {
  const meta = {
    cash_in: { title: "Uang Masuk", sub: "Tambahan uang ke cash drawer di luar penjualan.", needPin: false, cta: "Simpan Uang Masuk" },
    cash_out: { title: "Pengeluaran", sub: "Uang tunai keluar dari cash drawer.", needPin: true, cta: "Simpan Pengeluaran" },
    deposit: { title: "Setor Uang", sub: "Serahkan uang laci ke owner / penanggung jawab penerima. Tercatat final.", needPin: true, cta: "Simpan Setoran" },
  }[kind];
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState("");
  const [category, setCategory] = useState(KATEGORI_OUT[0]);
  const [receiver, setReceiver] = useState("");
  const [note, setNote] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const amt = parseInt(amount || 0);
    if (!amt || amt <= 0) return toast.error("Nominal wajib diisi");
    if (meta.needPin && pin.length !== 6) return toast.error("PIN 6 digit wajib untuk transaksi ini");
    if (kind === "deposit" && !receiver.trim()) return toast.error("Nama penerima wajib diisi");
    setBusy(true);
    try {
      await api.post("/pos/cash/mutations", {
        type: kind, amount: amt,
        note: [kind === "cash_in" && source ? `Sumber: ${source}` : "", note].filter(Boolean).join(" — "),
        category: kind === "cash_out" ? category : null,
        receiver_name: kind === "deposit" ? receiver.trim() : null,
        attendant_id: attendant?.id, attendant_name: attendant?.name, pin: pin || null,
      });
      toast.success(`${meta.title} ${formatIDR(amt)} tercatat`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={meta.title} sub={meta.sub} onClose={onClose} testid={`pos-mut-${kind}`}>
      <div className="text-xs text-slate-500 mb-3 bg-rose-50/60 rounded-lg px-3 py-2">Petugas: <b className="text-slate-800">{attendant?.name}</b> • {new Date().toLocaleString("id-ID")}</div>
      <label className="text-xs text-slate-500">Nominal (Rp)</label>
      <input data-testid="mut-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className={inp + " mb-3"} />
      {kind === "cash_in" && (
        <div className="mb-3"><label className="text-xs text-slate-500">Sumber Uang</label>
          <input data-testid="mut-source" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Mis. tambahan modal dari owner" className={inp} /></div>
      )}
      {kind === "cash_out" && (
        <div className="mb-3"><label className="text-xs text-slate-500">Kategori Pengeluaran</label>
          <select data-testid="mut-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inp}>
            {KATEGORI_OUT.map((k) => <option key={k} value={k}>{k}</option>)}
          </select></div>
      )}
      {kind === "deposit" && (
        <div className="mb-3"><label className="text-xs text-slate-500">Nama Penerima</label>
          <input data-testid="mut-receiver" value={receiver} onChange={(e) => setReceiver(e.target.value)} placeholder="Mis. Owner Kiyana" className={inp} /></div>
      )}
      <div className="mb-3"><label className="text-xs text-slate-500">Keterangan</label>
        <input data-testid="mut-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tulis keterangan..." className={inp} /></div>
      {meta.needPin && <div className="mb-3"><PinField value={pin} onChange={setPin} testid="mut-pin" /></div>}
      <button data-testid="mut-save" onClick={save} disabled={busy} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
        {busy ? "Menyimpan..." : meta.cta}
      </button>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Serah Terima Cash Drawer — kedua pihak verifikasi PIN/ID Card
// ---------------------------------------------------------------------------
function HandoverModal({ shift, onClose, onDone }) {
  const [emps, setEmps] = useState([]);
  const [newId, setNewId] = useState("");
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [physical, setPhysical] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const saldo = shift?.saldo_sistem ?? 0;
  const selisih = physical !== "" ? (parseInt(physical || 0) - saldo) : null;

  useEffect(() => { api.get("/pos/attendants").then((r) => setEmps(r.data.filter((x) => x.id !== shift?.responsible_id))).catch(() => {}); }, [shift]);

  const submit = async () => {
    if (oldPin.length !== 6 || newPin.length !== 6) return toast.error("Kedua pihak wajib isi PIN 6 digit");
    if (!newId) return toast.error("Pilih penanggung jawab baru");
    if (physical === "") return toast.error("Isi uang fisik hasil hitung");
    if (selisih !== 0 && !note.trim()) return toast.error("Ada selisih — keterangan wajib diisi");
    setBusy(true);
    try {
      const { data } = await api.post("/pos/shifts/handover", {
        old_employee_id: shift.responsible_id, old_pin: oldPin,
        new_employee_id: newId, new_pin: newPin,
        physical_cash: parseInt(physical || 0), note,
      });
      toast.success(`Serah terima berhasil • Penanggung jawab baru: ${data.new_name}${data.selisih !== 0 ? ` • selisih ${formatIDR(data.selisih)} diajukan sebagai penyesuaian` : " • Balance"}`);
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Serah terima gagal");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Serah Terima Cash Drawer" sub="Hanya untuk pergantian penanggung jawab uang — sesi kas tetap aktif." onClose={onClose} testid="pos-handover-modal" wide>
      <div className="bg-rose-50/60 rounded-lg px-3 py-2 text-xs mb-3">
        Penanggung jawab lama: <b className="text-slate-800">{shift?.responsible_name || shift?.cashier_name}</b>
      </div>
      <PinField value={oldPin} onChange={setOldPin} testid="handover-old-pin" />
      <div className="text-[10px] text-slate-400 mb-3 mt-0.5">PIN penanggung jawab lama</div>
      <label className="text-xs text-slate-500">Penanggung Jawab Baru</label>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-2 mt-1">
        {emps.map((e) => (
          <button key={e.id} data-testid={`handover-emp-${e.id}`} onClick={() => setNewId(e.id)}
            className={`flex-shrink-0 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${newId === e.id ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-slate-600"}`}>
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${newId === e.id ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-500"}`}>{initials(e.name)}</span>
            {e.name}
          </button>
        ))}
      </div>
      <PinField value={newPin} onChange={setNewPin} testid="handover-new-pin" />
      <div className="text-[10px] text-slate-400 mb-3 mt-0.5">PIN penanggung jawab baru</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-slate-50 rounded-lg px-3 py-2"><div className="text-[10px] text-slate-400">Saldo Kas Sistem</div><div className="font-bold text-slate-800 text-sm">{formatIDR(saldo)}</div></div>
        <div><label className="text-[10px] text-slate-400">Uang Fisik (hitung bersama)</label>
          <input data-testid="handover-physical" type="number" value={physical} onChange={(e) => setPhysical(e.target.value)} className={inp + " h-9"} /></div>
      </div>
      {selisih !== null && (
        <div data-testid="handover-selisih" className={`text-xs font-semibold rounded-lg px-3 py-2 mb-3 text-center ${selisih === 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
          {selisih === 0 ? "Balance — tidak ada selisih" : `Selisih ${selisih > 0 ? "lebih" : "kurang"} ${formatIDR(Math.abs(selisih))} — wajib keterangan & approval owner`}
        </div>
      )}
      <label className="text-xs text-slate-500">Catatan</label>
      <input data-testid="handover-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan serah terima..." className={inp + " mb-3"} />
      <button data-testid="handover-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
        {busy ? "Memproses..." : "Konfirmasi Serah Terima"}
      </button>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tutup Kasir
// ---------------------------------------------------------------------------
function TutupKasirModal({ shift, onClose, onDone }) {
  const [counted, setCounted] = useState("");
  const [deposit, setDeposit] = useState("");
  const [keep, setKeep] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const saldo = shift?.saldo_sistem ?? 0;
  const c = parseInt(counted || 0), d = parseInt(deposit || 0), k = parseInt(keep || 0);
  const selisih = counted !== "" ? c - saldo : null;
  const splitOk = counted === "" || d + k === c || (keep === "" && d <= c);

  const submit = async () => {
    if (counted === "") return toast.error("Isi uang fisik hasil hitung");
    if (pin.length !== 6) return toast.error("PIN 6 digit wajib");
    setBusy(true);
    try {
      const { data } = await api.post("/pos/shifts/close", {
        counted_cash: c, deposit_amount: d, keep_amount: keep === "" ? 0 : k,
        employee_id: shift.responsible_id, pin,
      });
      toast.success(`Kasir ${data.code} ditutup • Setoran ${formatIDR(data.deposit_amount)} • Disisakan ${formatIDR(data.keep_amount)} • ${data.balancing_status === "balance" ? "Balance" : "Selisih " + formatIDR(data.balancing)}`);
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal tutup kasir");
      setPin("");
    } finally { setBusy(false); }
  };

  const RowItem = ({ l, v, bold, neg }) => (
    <div className={`flex justify-between text-xs ${bold ? "font-bold text-slate-900 border-t border-rose-100 pt-1.5 mt-1" : "text-slate-600"}`}>
      <span>{l}</span><span className={neg ? "text-red-500" : ""}>{neg && v !== 0 ? "-" : ""}{formatIDR(Math.abs(v))}</span>
    </div>
  );

  return (
    <Modal title={`Tutup Kasir ${shift?.code || ""}`} sub={`Penanggung jawab: ${shift?.responsible_name || shift?.cashier_name}`} onClose={onClose} testid="pos-close-shift-modal" wide>
      <div className="bg-rose-50/50 rounded-xl p-3 space-y-1 mb-4">
        <RowItem l="Saldo Awal" v={shift?.starting_cash || 0} />
        <RowItem l="Penjualan Tunai" v={shift?.cash_sales || 0} />
        <RowItem l="Uang Masuk Lainnya" v={shift?.cash_in_other || 0} />
        <RowItem l="Pengeluaran / Setoran / Refund" v={shift?.cash_out_total || 0} neg />
        <RowItem l="Penjualan Non-Tunai (di luar laci)" v={shift?.noncash_sales || 0} />
        {shift?.carry_in ? <RowItem l="Saldo sesi sebelumnya / penyesuaian" v={shift.carry_in} /> : null}
        <RowItem l="Saldo Menurut Sistem (real-time)" v={saldo} bold />
      </div>
      <div className="space-y-2.5">
        <div><label className="text-xs text-slate-500">Uang fisik di laci (hasil hitung)</label>
          <input data-testid="pos-counted-cash" type="number" value={counted} onChange={(e) => setCounted(e.target.value)} className={inp} /></div>
        <div><label className="text-xs text-slate-500">Setoran akhir ke owner</label>
          <input data-testid="pos-deposit" type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)} className={inp} /></div>
        <div><label className="text-xs text-slate-500">Disisakan untuk modal kembalian (saldo awal sesi berikutnya)</label>
          <input data-testid="pos-keep-cash" type="number" value={keep} onChange={(e) => setKeep(e.target.value)} className={inp} /></div>
        <PinField value={pin} onChange={setPin} testid="pos-close-pin" />
      </div>
      {selisih !== null && (
        <div className={`mt-3 text-xs rounded-lg px-3 py-2 font-semibold text-center ${selisih === 0 ? "bg-emerald-50 text-emerald-600" : selisih > 0 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`} data-testid="close-selisih">
          Selisih (fisik vs sistem): {selisih === 0 ? "Balance" : `${selisih > 0 ? "+" : ""}${formatIDR(selisih)}`}
          {!splitOk && <div className="text-red-500 font-normal mt-0.5">Setoran + sisa kembalian harus sama dengan uang fisik ({formatIDR(c)})</div>}
        </div>
      )}
      <button data-testid="pos-confirm-close-shift" onClick={submit} disabled={busy} className="mt-4 w-full h-12 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
        {busy ? "Memproses..." : "Tutup Kasir & Setor"}
      </button>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Void transaksi (cari no. pesanan / nama pelanggan)
// ---------------------------------------------------------------------------
function VoidModal({ attendant, onClose, onDone }) {
  const [txns, setTxns] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [reason, setReason] = useState("");
  const [refundCash, setRefundCash] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/pos/transactions", { params: q.trim() ? { q: q.trim() } : {} })
        .then((r) => setTxns(r.data.filter((x) => x.status !== "voided").slice(0, 30)));
    }, q.trim() ? 250 : 0);
    return () => clearTimeout(t);
  }, [q]);

  const doVoid = async () => {
    if (!sel) return toast.error("Pilih pesanan dulu");
    if (!reason.trim()) return toast.error("Alasan void wajib diisi");
    if (pin.length !== 6) return toast.error("PIN 6 digit wajib (otorisasi pembatalan)");
    setBusy(true);
    try {
      const { data } = await api.post(`/pos/transactions/${sel.id}/void`, {
        reason, refund_cash: sel.payment_method !== "cash" && refundCash,
        employee_id: attendant?.id, pin,
      });
      toast.success(`Pesanan ${sel.code} di-void${data.refund_cash_amount ? ` • refund tunai ${formatIDR(data.refund_cash_amount)} dari laci` : ""}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal void");
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Void Transaksi" sub="Pesanan void keluar dari laporan. Tunai otomatis refund dari laci; non-tunai opsional refund tunai." onClose={onClose} testid="pos-void-modal">
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="pos-void-search" value={q} onChange={(e) => { setQ(e.target.value); setSel(null); }}
          placeholder="Ketik no. pesanan / nama pelanggan..." className={inp + " pl-9"} />
      </div>
      <div className="max-h-52 overflow-y-auto divide-y divide-slate-100 border border-rose-100 rounded-lg mb-3">
        {txns.map((t) => (
          <button key={t.id} data-testid={`pos-void-pick-${t.code}`} onClick={() => { setSel(t); setRefundCash(false); }}
            className={`w-full text-left px-3 py-2.5 text-xs hover:bg-rose-50/50 ${sel?.id === t.id ? "bg-red-50" : ""}`}>
            <div className="flex justify-between"><span className="font-semibold text-slate-800">{t.code}</span><span className="font-bold text-slate-900">{formatIDR(t.total)}</span></div>
            <div className="text-[10px] text-slate-500">{t.customer_name || "Umum"} • {(t.payment_method || "").toUpperCase()} • {formatTimeID(t.created_at)}</div>
          </button>
        ))}
        {txns.length === 0 && <div data-testid="pos-void-empty" className="px-3 py-6 text-center text-xs text-slate-400">{q.trim() ? `Tidak ditemukan pesanan "${q.trim()}".` : "Tidak ada transaksi yang bisa di-void."}</div>}
      </div>
      {sel && sel.payment_method === "cash" && (
        <div className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3">Pembayaran TUNAI — {formatIDR(sel.total)} otomatis dikembalikan dari laci sebagai Refund Tunai.</div>
      )}
      {sel && sel.payment_method !== "cash" && (
        <label className="flex items-start gap-2 text-xs text-slate-700 mb-3 bg-slate-50 rounded-lg px-3 py-2 cursor-pointer">
          <input data-testid="pos-void-refund-cash" type="checkbox" checked={refundCash} onChange={(e) => setRefundCash(e.target.checked)} className="mt-0.5" />
          <span>Refund tunai dari laci sebesar {formatIDR(sel.cash_amount || sel.total)} (pembayaran {(sel.mixed_method || sel.payment_method).toUpperCase()} tidak masuk laci — centang agar saldo tetap balance)</span>
        </label>
      )}
      <input data-testid="pos-void-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan void (wajib)..." className={inp + " mb-3"} />
      <div className="mb-3"><PinField value={pin} onChange={setPin} testid="pos-void-pin" /></div>
      <button data-testid="pos-void-confirm" onClick={doVoid} disabled={busy || !sel} className="w-full h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
        {busy ? "Memproses..." : "Void Pesanan"}
      </button>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Refund Tunai (wajib pesanan void) — cari no. pesanan
// ---------------------------------------------------------------------------
function RefundModal({ attendant, onClose, onDone }) {
  const [voided, setVoided] = useState([]);
  const [refQ, setRefQ] = useState("");
  const [refTxn, setRefTxn] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { status: "voided" };
      if (refQ.trim()) params.q = refQ.trim();
      api.get("/pos/transactions", { params }).then((r) => setVoided(r.data.filter((x) => !x.refunded)));
    }, refQ.trim() ? 250 : 0);
    return () => clearTimeout(t);
  }, [refQ]);

  const save = async () => {
    const amt = parseInt(amount || 0);
    if (!refTxn) return toast.error("Pilih pesanan yang sudah di-void");
    if (!amt || amt <= 0) return toast.error("Nominal wajib diisi");
    if (pin.length !== 6) return toast.error("PIN 6 digit wajib");
    setBusy(true);
    try {
      await api.post("/pos/cash/mutations", { type: "refund_cash", amount: amt, note, ref_txn_id: refTxn, attendant_id: attendant?.id, attendant_name: attendant?.name, pin });
      toast.success("Refund tunai tercatat");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Refund Tunai" sub="Pengembalian uang tunai ke pelanggan dari cash drawer. Wajib pilih pesanan yang sudah di-void." onClose={onClose} testid="pos-refund-modal">
      <label className="text-xs text-slate-500">Pesanan yang sudah di-void</label>
      <input data-testid="pos-refund-search" value={refQ} onChange={(e) => setRefQ(e.target.value)} placeholder="Ketik no. pesanan / nama pelanggan..." className={inp + " mb-2 mt-1"} />
      <select data-testid="pos-refund-txn" value={refTxn} onChange={(e) => { setRefTxn(e.target.value); const t = voided.find((x) => x.id === e.target.value); if (t) setAmount(String(t.cash_amount || t.total)); }} className={inp + " mb-3"}>
        <option value="">— Pilih pesanan void —</option>
        {voided.map((t) => <option key={t.id} value={t.id}>{t.code} • {t.customer_name || "Umum"} • {formatIDR(t.total)} • {(t.payment_method || "").toUpperCase()}</option>)}
      </select>
      {voided.length === 0 && <div className="text-[11px] text-amber-600 bg-amber-50 rounded-lg px-2.5 py-1.5 mb-3">Tidak ada pesanan void yang belum di-refund. Void dulu dari tombol Void.</div>}
      <label className="text-xs text-slate-500">Nominal (Rp)</label>
      <input data-testid="pos-refund-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inp + " mb-3"} />
      <label className="text-xs text-slate-500">Keterangan</label>
      <input data-testid="pos-refund-note" value={note} onChange={(e) => setNote(e.target.value)} className={inp + " mb-3"} />
      <div className="mb-3"><PinField value={pin} onChange={setPin} testid="pos-refund-pin" /></div>
      <button data-testid="pos-refund-save" onClick={save} disabled={busy} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
        {busy ? "Menyimpan..." : "Simpan Refund"}
      </button>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tambah pelanggan cepat (nama, no WA, alamat & email opsional)
// ---------------------------------------------------------------------------
function CustAddModal({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Nama wajib diisi");
    if (!phone.trim()) return toast.error("No WA wajib diisi");
    setBusy(true);
    try {
      const { data } = await api.post("/pos/customers", { name: name.trim(), phone: phone.trim(), address: address.trim() || null, email: email.trim() || null });
      toast.success(`Pelanggan ${data.name} tersimpan`);
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pelanggan");
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Tambah Pelanggan Cepat" sub="Data langsung bisa dipakai untuk transaksi ini" onClose={onClose} testid="pos-cust-add-modal">
      <div className="space-y-3">
        <div><label className="text-xs text-slate-500">Nama *</label>
          <input data-testid="cust-add-name" value={name} onChange={(e) => setName(e.target.value)} className={inp} placeholder="Nama pelanggan" /></div>
        <div><label className="text-xs text-slate-500">No WA *</label>
          <input data-testid="cust-add-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inp} placeholder="08xxxxxxxxxx" /></div>
        <div><label className="text-xs text-slate-500">Alamat (opsional)</label>
          <input data-testid="cust-add-address" value={address} onChange={(e) => setAddress(e.target.value)} className={inp} placeholder="Alamat singkat" /></div>
        <div><label className="text-xs text-slate-500">Email (opsional)</label>
          <input data-testid="cust-add-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} placeholder="email@contoh.com" /></div>
        <button data-testid="cust-add-save" onClick={save} disabled={busy} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
          {busy ? "Menyimpan..." : "Simpan & Pilih Pelanggan"}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Terminal utama
// ---------------------------------------------------------------------------
export default function POSTerminal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = ADMIN_LIKE.includes(user?.role);

  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [cart, setCart] = useState(() => loadLS("kiyana_pos_cart", []));
  const [payment, setPayment] = useState("cash");
  const [mixed, setMixed] = useState(false);
  const [mixedCash, setMixedCash] = useState("");
  const [received, setReceived] = useState("");
  const [discount, setDiscount] = useState("");
  const [discMode, setDiscMode] = useState("rp");
  const [receipt, setReceipt] = useState(null);
  const [shift, setShift] = useState(undefined);
  const [lastKeep, setLastKeep] = useState(0);
  const [attendant, setAttendant] = useState(() => loadLS("kiyana_pos_att", null));
  const [locked, setLocked] = useState(false);
  const [lockMin, setLockMin] = useState(10);
  const [modal, setModal] = useState(null);
  const [paying, setPaying] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const [holds, setHolds] = useState(() => loadLS("kiyana_pos_holds", []));
  const [customer, setCustomer] = useState(null);
  const [custQ, setCustQ] = useState("");
  const [custOpts, setCustOpts] = useState([]);
  const lastAct = useRef(Date.now());

  useEffect(() => { api.get("/products", { params: { limit: 300, pos: 1 } }).then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/pos/config").then((r) => setLockMin(r.data.pos_lock_minutes || 10)).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem("kiyana_pos_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("kiyana_pos_holds", JSON.stringify(holds)); }, [holds]);

  const loadShift = async () => {
    try {
      const { data } = await api.get("/pos/shifts/current");
      setShift(data.shift);
      setLastKeep(data.last_keep || 0);
      if (!data.shift) setModal("open");
    } catch { setShift(null); }
  };
  useEffect(() => { loadShift(); }, []);

  // Auto Lock — aktivitas apa pun memperbarui timer
  useEffect(() => {
    const bump = () => { lastAct.current = Date.now(); };
    const evs = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(() => {
      if (!locked && attendant && shift && Date.now() - lastAct.current > lockMin * 60000) doLock();
    }, 5000);
    return () => { evs.forEach((e) => window.removeEventListener(e, bump)); clearInterval(t); };
  }, [locked, attendant, shift, lockMin]);

  const doLock = async () => {
    if (attendant?.session_id) {
      try { await api.post("/pos/attendant/lock", { session_id: attendant.session_id }); } catch { /* abaikan */ }
    }
    localStorage.removeItem("kiyana_pos_att");
    setAttendant(null);
    setLocked(true);
    setAttOpen(false);
  };

  const unlock = (att, sessionId) => {
    const a = { ...att, session_id: sessionId };
    localStorage.setItem("kiyana_pos_att", JSON.stringify(a));
    setAttendant(a);
    setLocked(false);
    lastAct.current = Date.now();
  };

  const openOp = (m) => {
    if (!attendant) { setLocked(true); return toast.error("Login petugas dulu (scan kartu / PIN)"); }
    setModal(m);
  };

  // Keranjang
  const filtered = useMemo(() =>
    products.filter((p) => (cat === "all" || p.category === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase())))
  , [products, q, cat]);

  const cartKey = (id, variant) => `${id}|${variant || ""}`;
  const add = (p, variantInfo = null) => setCart((c) => {
    const key = cartKey(p.id ?? p.product_id, variantInfo?.variant);
    const f = c.find((x) => x.key === key);
    return f
      ? c.map((x) => x.key === key ? { ...x, qty: x.qty + 1 } : x)
      : [...c, {
          key, id: p.id ?? p.product_id, name: p.name,
          price: variantInfo?.price ?? p.price, qty: 1,
          image: variantInfo?.image ?? p.image,
          variant: variantInfo?.variant || null, sku: variantInfo?.sku || null,
        }];
  });
  const inc = (key) => setCart((c) => c.map((x) => x.key === key ? { ...x, qty: x.qty + 1 } : x));
  const dec = (key) => setCart((c) => c.map((x) => x.key === key ? { ...x, qty: Math.max(1, x.qty - 1) } : x));
  const rem = (key) => setCart((c) => c.filter((x) => x.key !== key));

  const scanBarcode = async (code) => {
    const bc = (code || "").trim();
    if (!bc) return;
    try {
      const { data } = await api.get(`/pos/barcode/${encodeURIComponent(bc)}`);
      if (data.stock <= 0) { toast.error(`Stok ${data.name} (${data.variant}) habis`); return; }
      add(data, data);
      toast.success(`${data.name} • ${data.variant} • ${formatIDR(data.price)}`);
      setQ("");
    } catch (e) {
      const st = e?.response?.status;
      toast.error(st === 404 ? "Barcode Tidak Ditemukan" : (e?.response?.data?.detail || "Gagal membaca barcode"));
    }
  };

  const subtotal = cart.reduce((s, x) => s + x.price * x.qty, 0);
  const discInput = Math.max(0, parseFloat(discount || 0) || 0);
  const disc = discMode === "pct" ? Math.round(subtotal * Math.min(discInput, 100) / 100) : Math.min(subtotal, discInput);
  const total = Math.max(0, subtotal - disc);
  const cashPart = mixed ? (parseInt(mixedCash || 0) || 0) : total;
  const change = Math.max(0, (parseInt(received || 0) || 0) - cashPart);

  const pay = async () => {
    if (paying) return;
    if (!attendant) { setLocked(true); return toast.error("Login petugas dulu"); }
    if (cart.length === 0) return toast.error("Keranjang kosong");
    const amt = parseInt(received || 0) || 0;
    if (payment === "cash" && !mixed && amt < total) return toast.error("Uang diterima kurang");
    if (mixed) {
      if (payment === "cash") return toast.error("Pilih metode non-tunai untuk pembayaran campuran");
      if (cashPart <= 0 || cashPart >= total) return toast.error("Bagian tunai harus lebih dari nol dan kurang dari total");
      if (amt < cashPart) return toast.error("Uang diterima kurang dari bagian tunai");
    }
    setPaying(true);
    try {
      const { data } = await api.post("/pos/transactions", {
        items: cart.map((x) => ({ product_id: x.id, variant: x.variant || null, qty: x.qty, price: x.price, discount: 0 })),
        payment_method: mixed ? "mixed" : payment,
        mixed_method: mixed ? payment : null,
        cash_amount: mixed ? cashPart : null,
        amount_received: mixed ? amt : (payment === "cash" ? amt : total),
        discount_total: disc,
        customer_id: customer?.id || null,
        attendant_id: attendant.id, attendant_name: attendant.name,
      });
      setReceipt(data);
      setCart([]); setReceived(""); setMixedCash(""); setMixed(false); setDiscount(""); setCustomer(null);
      toast.success(`Transaksi ${data.code} berhasil • ${attendant.name}`);
      loadShift();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menyimpan transaksi — coba lagi atau hubungi owner";
      toast.error(msg);
      if (msg.toLowerCase().includes("kasir")) setModal("open");
    } finally { setPaying(false); }
  };

  const holdCart = () => {
    if (cart.length === 0) return toast.error("Keranjang kosong");
    setHolds((h) => [...h, { id: Date.now(), at: new Date().toISOString(), items: cart, customer, discount: disc, by: attendant?.name }]);
    setCart([]); setDiscount(""); setCustomer(null);
    toast.success("Transaksi disimpan sementara");
  };
  const resumeHold = (h) => {
    setCart(h.items); setCustomer(h.customer); setDiscount(h.discount ? String(h.discount) : "");
    setHolds((x) => x.filter((y) => y.id !== h.id));
    setModal(null);
  };

  const searchCust = async (val) => {
    setCustQ(val);
    try { const { data } = await api.get("/pos/customer-lookup", { params: { q: val } }); setCustOpts(data); } catch { /* abaikan */ }
  };

  const OPS = [
    { k: "cash_in", label: "Uang Masuk", Icon: ArrowDownCircle, cls: "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100", testid: "pos-op-cash-in" },
    { k: "cash_out", label: "Pengeluaran", Icon: ArrowUpCircle, cls: "bg-red-50 text-red-500 border-red-200 hover:bg-red-100", testid: "pos-op-cash-out" },
    { k: "deposit", label: "Setor Uang", Icon: Landmark, cls: "bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100", testid: "pos-op-deposit" },
    { k: "handover", label: "Serah Terima", Icon: Repeat, cls: "bg-violet-50 text-violet-600 border-violet-200 hover:bg-violet-100", testid: "pos-op-handover" },
    { k: "close", label: "Tutup Kasir", Icon: LockKeyhole, cls: "bg-slate-800 text-white border-slate-800 hover:bg-slate-900", testid: "pos-op-close" },
  ];

  const MENUS = [
    { label: "Dashboard", Icon: LayoutDashboard, to: "/admin/pos/dashboard", show: isAdmin },
    { label: "Input Kasir", Icon: ShoppingCart, active: true, show: true },
    { label: "Penjualan Produk", Icon: Home, to: "/admin/pos/sales", show: isAdmin },
    { label: "Laporan Penjualan", Icon: BarChart3, to: isAdmin ? "/admin/pos/reports" : "/pos/reports", show: true },
    { label: "Data Pelanggan", Icon: Users, to: "/admin/pos/customers", show: isAdmin },
  ];

  return (
    <div className="h-screen flex bg-[#FBF7F4] text-slate-800 overflow-hidden" data-testid="pos-terminal">
      {/* Sidebar */}
      <aside className="w-52 flex-shrink-0 bg-white border-r border-rose-100 hidden md:flex flex-col">
        <div className="px-4 py-4 flex items-center gap-2.5 border-b border-rose-50">
          <div className="w-9 h-9 bg-rose-600 rounded-xl flex items-center justify-center"><Store className="w-5 h-5 text-white" /></div>
          <div>
            <div className="font-display font-bold text-sm text-slate-900 leading-none">KIYANA <span className="text-rose-600">POS</span></div>
            <div className="text-[9px] text-slate-400 uppercase tracking-widest mt-0.5">by Cirebon Cloth</div>
          </div>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-1">
          {MENUS.filter((m) => m.show).map((m) => (
            <button key={m.label} data-testid={`pos-menu-${m.label.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => m.to && nav(m.to)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-colors ${m.active ? "bg-rose-600 text-white" : "text-slate-600 hover:bg-rose-50"}`}>
              <m.Icon className="w-4 h-4" /> {m.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-rose-50 text-[10px] text-slate-400">Toko Fisik Cirebon</div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 bg-white border-b border-rose-100 flex items-center justify-between px-4 flex-shrink-0">
          <div className="font-display font-bold text-slate-900">Input Kasir</div>
          <div className="flex items-center gap-3">
            <span data-testid="pos-shift-status" className={`text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 ${shift ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${shift ? "bg-emerald-500" : "bg-slate-400"}`} />
              {shift ? "Shift Aktif" : "Shift Belum Aktif"}
            </span>
            {shift && <span className="hidden lg:flex items-center gap-1 text-[11px] text-slate-400"><Clock className="w-3 h-3" /> {shift.code} • sejak {formatTimeID(shift.opened_at)}</span>}
            <div className="relative">
              <button data-testid="pos-attendant-chip" onClick={() => setAttOpen((o) => !o)} className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-rose-100 hover:border-rose-300 transition-colors">
                <span className="w-7 h-7 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-[10px] font-bold">{initials(attendant?.name || user?.name)}</span>
                <span className="text-xs font-medium text-slate-700 max-w-[110px] truncate">{attendant?.name || "Belum ada petugas"}</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
              {attOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAttOpen(false)} />
                  <div className="absolute right-0 top-11 z-50 bg-white border border-rose-100 rounded-xl shadow-lg w-56 py-1.5" data-testid="pos-attendant-menu">
                    <button data-testid="pos-lock-btn" onClick={doLock} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-rose-50 flex items-center gap-2">
                      <LockKeyhole className="w-3.5 h-3.5 text-rose-500" /> Ganti Petugas / Kunci Kasir
                    </button>
                    <button data-testid="pos-logout-btn" onClick={() => { doLock(); logout(); nav("/login"); }} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-rose-50 flex items-center gap-2">
                      <LogOut className="w-3.5 h-3.5 text-slate-400" /> Keluar Akun Terminal
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 flex min-h-0">
          {/* Kiri: operasional + katalog + keranjang */}
          <div className="flex-1 flex flex-col p-3 gap-2.5 min-w-0 min-h-0">
            {/* 5 tombol operasional + void/refund */}
            <div className="flex gap-2 flex-shrink-0" data-testid="pos-op-buttons">
              {OPS.map((o) => (
                <button key={o.k} data-testid={o.testid} onClick={() => openOp(o.k)}
                  className={`flex-1 h-11 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${o.cls}`}>
                  <o.Icon className="w-4 h-4" /> <span className="hidden xl:inline">{o.label}</span>
                </button>
              ))}
              <button data-testid="pos-op-void" onClick={() => openOp("void")} title="Void Transaksi" className="h-11 w-11 flex-shrink-0 rounded-xl border border-red-200 bg-white text-red-500 flex items-center justify-center hover:bg-red-50 transition-colors"><Ban className="w-4 h-4" /></button>
              <button data-testid="pos-op-refund" onClick={() => openOp("refund")} title="Refund Tunai" className="h-11 w-11 flex-shrink-0 rounded-xl border border-orange-200 bg-white text-orange-500 flex items-center justify-center hover:bg-orange-50 transition-colors"><RotateCcw className="w-4 h-4" /></button>
            </div>

            {/* Pencarian */}
            <div className="relative flex-shrink-0">
              <ScanLine className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="pos-search" value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scanBarcode(q); } }}
                placeholder="Scan barcode atau cari nama/SKU produk..."
                className="w-full h-10 pl-10 pr-14 rounded-xl bg-white border border-rose-100 text-sm focus:outline-none focus:border-rose-400 shadow-sm" />
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">F2</span>
            </div>

            {/* Kategori */}
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar flex-shrink-0">
              {CATS.map((c) => (
                <button key={c.id} onClick={() => setCat(c.id)}
                  className={`px-3.5 h-7 flex-shrink-0 rounded-full text-[11px] font-medium transition-colors ${cat === c.id ? "bg-rose-600 text-white" : "bg-white text-slate-600 border border-rose-100"}`}>
                  {c.name}
                </button>
              ))}
            </div>

            {/* Katalog — area ini yang scroll, kartu produk kecil */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 pb-1">
                {filtered.map((p) => (
                  <button key={p.id} data-testid={`pos-add-${p.id}`} onClick={() => add(p)}
                    className="bg-white border border-rose-100 rounded-lg overflow-hidden text-left hover:border-rose-300 hover:shadow-sm active:scale-[0.98] transition">
                    <div className="h-20 bg-rose-50/40">{p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />}</div>
                    <div className="p-1.5">
                      <div className="text-[10px] text-slate-800 font-medium line-clamp-1">{p.name}</div>
                      <div className="text-rose-600 font-bold text-xs mt-0.5">{formatIDR(p.price)}</div>
                      <div className="text-[8px] text-slate-400">SKU: {p.id.slice(0, 6).toUpperCase()} • Stok {p.stock}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Keranjang — fix di layar, isi scroll internal */}
            <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden flex-shrink-0" data-testid="pos-cart-table">
              <div className="px-4 py-2 border-b border-rose-50 flex items-center justify-between">
                <div className="font-display font-semibold text-sm text-slate-900 flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-rose-500" /> Keranjang Belanja</div>
                {cart.length > 0 && (
                  <button data-testid="pos-cart-clear" onClick={() => setCart([])} className="text-[11px] text-red-500 hover:text-red-600 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Hapus Semua</button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-rose-50">
                    <th className="px-4 py-2 font-medium">Produk</th>
                    <th className="px-2 py-2 font-medium">Varian</th>
                    <th className="px-2 py-2 font-medium text-center">Qty</th>
                    <th className="px-2 py-2 font-medium text-right">Harga</th>
                    <th className="px-2 py-2 font-medium text-right">Subtotal</th>
                    <th className="px-4 py-2 font-medium w-10">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((it) => (
                    <tr key={it.key} className="border-b border-rose-50/60">
                      <td className="px-4 py-1.5">
                        <div className="flex items-center gap-2.5">
                          {it.image && <img src={it.image} alt="" className="w-7 h-7 rounded-lg object-cover bg-rose-50" />}
                          <div>
                            <div className="text-xs font-medium text-slate-800 line-clamp-1">{it.name}</div>
                            <div className="text-[9px] text-slate-400">SKU: {it.sku || it.id.slice(0, 6).toUpperCase()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-[11px] text-slate-500">{it.variant || "Standar"}</td>
                      <td className="px-2 py-2.5">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => dec(it.key)} className="w-6 h-6 rounded-md bg-rose-50 text-slate-600 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                          <span className="w-7 text-center text-xs font-semibold">{it.qty}</span>
                          <button data-testid={`pos-inc-${it.key}`} onClick={() => inc(it.key)} className="w-6 h-6 rounded-md bg-rose-50 text-slate-600 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-right text-xs text-slate-600">{formatIDR(it.price)}</td>
                      <td className="px-2 py-2.5 text-right text-xs font-bold text-slate-900">{formatIDR(it.price * it.qty)}</td>
                      <td className="px-4 py-2.5"><button onClick={() => rem(it.key)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                  {cart.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-xs text-slate-400">Belum ada item — klik produk di katalog.</td></tr>}
                </tbody>
              </table>
              </div>
            </div>
          </div>

          {/* Kanan: pembayaran */}
          <aside className="w-80 lg:w-96 flex-shrink-0 bg-white border-l border-rose-100 overflow-y-auto p-3 hidden md:flex md:flex-col" data-testid="pos-payment-panel">
            {/* Pelanggan — kanan atas + tambah cepat */}
            <div className="mb-3">
              <div className="text-[11px] text-slate-400 mb-1.5 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Pelanggan</div>
              <div className="flex gap-1.5">
                <button data-testid="pos-customer-btn" onClick={() => { setModal("cust"); searchCust(""); }}
                  className="flex-1 h-10 rounded-xl border border-rose-100 bg-rose-50/40 text-left px-3 text-xs text-slate-700 hover:border-rose-300 transition-colors truncate">
                  {customer ? `${customer.name}${customer.phone ? ` • ${customer.phone}` : ""}` : "Pelanggan Umum"}
                </button>
                <button data-testid="pos-cust-add" onClick={() => setModal("custAdd")} title="Tambah pelanggan cepat"
                  className="h-10 w-10 flex-shrink-0 rounded-xl bg-rose-600 text-white flex items-center justify-center hover:bg-rose-700 transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="space-y-3 flex-1 flex flex-col">
            <div>
              <div className="font-display font-semibold text-sm text-slate-900 mb-2 flex items-center gap-2"><Wallet className="w-4 h-4 text-rose-500" /> Ringkasan Pembayaran</div>
              <div className="space-y-1.5 text-xs text-slate-600">
                <div className="flex justify-between"><span>Subtotal ({cart.reduce((s, x) => s + x.qty, 0)} produk)</span><span>{formatIDR(subtotal)}</span></div>
                <div className="flex justify-between items-center gap-2">
                  <span>Diskon</span>
                  <div className="flex items-center gap-1">
                    <input data-testid="pos-discount" type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0"
                      className="w-20 h-7 px-2 rounded-lg border border-rose-100 bg-rose-50/40 text-right text-xs focus:outline-none focus:border-rose-400" />
                    <div className="flex rounded-lg border border-rose-100 overflow-hidden flex-shrink-0">
                      <button data-testid="pos-disc-rp" onClick={() => setDiscMode("rp")} className={`h-7 px-2 text-[10px] font-bold transition-colors ${discMode === "rp" ? "bg-rose-600 text-white" : "bg-white text-slate-500"}`}>Rp</button>
                      <button data-testid="pos-disc-pct" onClick={() => setDiscMode("pct")} className={`h-7 px-2 text-[10px] font-bold transition-colors ${discMode === "pct" ? "bg-rose-600 text-white" : "bg-white text-slate-500"}`}>%</button>
                    </div>
                  </div>
                </div>
                {disc > 0 && <div className="flex justify-between text-rose-600 font-semibold"><span>Potongan{discMode === "pct" ? ` (${discInput}%)` : ""}</span><span>-{formatIDR(disc)}</span></div>}
              </div>
              <div className="mt-2 bg-rose-50 border border-rose-100 rounded-xl px-3.5 py-2.5 flex items-center justify-between">
                <span className="text-[11px] font-bold text-rose-500 uppercase">Total Bayar</span>
                <span data-testid="pos-total" className="text-lg font-bold text-rose-600">{formatIDR(total)}</span>
              </div>
            </div>

            <div>
              <div className="text-[11px] text-slate-400 mb-1.5 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Metode Pembayaran</div>
              <div className="grid grid-cols-4 gap-1.5">
                {PMETA.map((m) => (
                  <button key={m.id} data-testid={`pos-pay-${m.id}`} onClick={() => setPayment(m.id)}
                    className={`h-12 rounded-xl border flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors ${payment === m.id ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-slate-500 hover:border-rose-200"}`}>
                    <m.icon className="w-4 h-4" /> {m.label}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 mt-2.5 text-xs text-slate-600 cursor-pointer">
                <input data-testid="pos-mixed" type="checkbox" checked={mixed} onChange={(e) => setMixed(e.target.checked)} className="accent-rose-600" />
                Pembayaran Campuran
              </label>
              {mixed && (
                <div className="mt-2 bg-blue-50/60 border border-blue-100 rounded-xl p-3 space-y-1.5">
                  <label className="text-[10px] text-slate-500">Bagian Tunai (masuk laci)</label>
                  <input data-testid="pos-mixed-cash" type="number" value={mixedCash} onChange={(e) => setMixedCash(e.target.value)} placeholder="0"
                    className="w-full h-10 px-3 rounded-lg border border-blue-200 bg-white text-xs focus:outline-none focus:border-blue-400" />
                  <div className="text-[10px] text-slate-500">Sisa {formatIDR(Math.max(0, total - cashPart))} dibayar via {(PMETA.find((m) => m.id === payment) || {}).label || "-"}</div>
                </div>
              )}
            </div>

            {(payment === "cash" || mixed) && (
              <div>
                <div className="text-[11px] text-slate-400 mb-1.5">Uang Diterima</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Rp</span>
                  <input data-testid="pos-received" type="number" value={received} onChange={(e) => setReceived(e.target.value)} placeholder="0"
                    className="flex-1 h-11 px-3 rounded-xl border border-rose-100 bg-rose-50/40 text-sm focus:outline-none focus:border-rose-400" />
                </div>
                {change > 0 && (
                  <div className="mt-2 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2.5 flex items-center justify-between">
                    <span className="text-[11px] text-emerald-600">Kembalian</span>
                    <span data-testid="pos-change" className="font-bold text-emerald-600">{formatIDR(change)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 min-h-2" />
            <div className="space-y-2 pt-0.5">
              <button data-testid="pos-pay-btn" onClick={pay} disabled={cart.length === 0 || paying}
                className="w-full h-14 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors">
                <Printer className="w-4 h-4" /> {paying ? "Memproses..." : "Proses Pembayaran"}
              </button>
              <button data-testid="pos-hold-btn" onClick={holdCart} disabled={cart.length === 0}
                className="w-full h-11 rounded-xl border border-rose-200 text-rose-700 font-semibold text-sm disabled:opacity-40 flex items-center justify-center gap-2 hover:bg-rose-50 transition-colors">
                <Save className="w-4 h-4" /> Simpan Transaksi
              </button>
              {holds.length > 0 && (
                <button data-testid="pos-holds-btn" onClick={() => setModal("holds")}
                  className="w-full h-9 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold flex items-center justify-center gap-1.5">
                  <FolderOpen className="w-3.5 h-3.5" /> Buka Simpanan ({holds.length})
                </button>
              )}
            </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile: bar bawah */}
      <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-rose-100 p-3 z-40">
        <button data-testid="pos-pay-btn-mobile" onClick={pay} disabled={cart.length === 0 || paying}
          className="w-full h-12 rounded-xl bg-rose-600 text-white font-bold text-sm disabled:opacity-40">
          Bayar {formatIDR(total)}
        </button>
      </div>

      {/* Modals */}
      {modal === "open" && !shift && <BukaKasirModal lastKeep={lastKeep} onDone={(s) => { setModal(null); loadShift(); if (s.attendant && s.session_id) unlock(s.attendant, s.session_id); }} />}
      {modal === "cash_in" && <KasirMutModal kind="cash_in" attendant={attendant} onClose={() => setModal(null)} onDone={() => { setModal(null); loadShift(); }} />}
      {modal === "cash_out" && <KasirMutModal kind="cash_out" attendant={attendant} onClose={() => setModal(null)} onDone={() => { setModal(null); loadShift(); }} />}
      {modal === "deposit" && <KasirMutModal kind="deposit" attendant={attendant} onClose={() => setModal(null)} onDone={() => { setModal(null); loadShift(); }} />}
      {modal === "handover" && <HandoverModal shift={shift} onClose={() => setModal(null)} onDone={() => { setModal(null); loadShift(); doLock(); }} />}
      {modal === "close" && <TutupKasirModal shift={shift} onClose={() => setModal(null)} onDone={() => { setModal(null); setAttendant(null); localStorage.removeItem("kiyana_pos_att"); loadShift(); }} />}
      {modal === "void" && <VoidModal attendant={attendant} onClose={() => setModal(null)} onDone={() => { setModal(null); loadShift(); }} />}
      {modal === "refund" && <RefundModal attendant={attendant} onClose={() => setModal(null)} onDone={() => { setModal(null); loadShift(); }} />}

      {modal === "custAdd" && <CustAddModal onClose={() => setModal(null)} onDone={(c) => { setCustomer(c); setModal(null); }} />}

      {modal === "cust" && (
        <Modal title="Pilih Pelanggan" sub="Opsional — untuk riwayat belanja" onClose={() => setModal(null)} testid="pos-customer-modal">
          <input data-testid="pos-customer-search" value={custQ} onChange={(e) => searchCust(e.target.value)} placeholder="Cari nama / HP / email..." className={inp + " mb-2"} />
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            <button data-testid="pos-customer-umum" onClick={() => { setCustomer(null); setModal(null); }} className="w-full text-left px-3 py-2.5 hover:bg-rose-50/50 text-sm text-slate-700 font-medium">Pelanggan Umum</button>
            {custOpts.map((c) => (
              <button key={c.id} data-testid={`pos-customer-opt-${c.id}`} onClick={() => { setCustomer(c); setModal(null); }} className="w-full text-left px-3 py-2.5 hover:bg-rose-50/50">
                <div className="text-sm text-slate-800 font-medium">{c.name}</div>
                <div className="text-[11px] text-slate-500">{c.phone || c.email}</div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {modal === "holds" && (
        <Modal title="Transaksi Tersimpan" sub="Lanjutkan atau hapus transaksi yang disimpan" onClose={() => setModal(null)} testid="pos-holds-modal">
          <div className="space-y-2">
            {holds.map((h) => (
              <div key={h.id} className="flex items-center justify-between border border-rose-100 rounded-xl px-3 py-2.5" data-testid={`pos-hold-${h.id}`}>
                <div>
                  <div className="text-xs font-semibold text-slate-800">{h.items.length} produk • {formatIDR(h.items.reduce((s, x) => s + x.price * x.qty, 0))}</div>
                  <div className="text-[10px] text-slate-400">{h.customer?.name || "Umum"} • {h.by || "-"} • {formatTimeID(h.at)}</div>
                </div>
                <div className="flex gap-1.5">
                  <button data-testid={`pos-hold-resume-${h.id}`} onClick={() => resumeHold(h)} className="h-8 px-3 rounded-lg bg-rose-600 text-white text-xs font-semibold">Lanjutkan</button>
                  <button onClick={() => setHolds((x) => x.filter((y) => y.id !== h.id))} className="h-8 w-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
            {holds.length === 0 && <div className="text-center text-xs text-slate-400 py-6">Tidak ada simpanan.</div>}
          </div>
        </Modal>
      )}

      {/* Struk */}
      {receipt && (
        <div className="fixed inset-0 bg-slate-900/40 z-[95] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6" data-testid="pos-receipt-modal">
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-2"><Printer className="w-6 h-6 text-emerald-600" /></div>
              <div className="font-display text-2xl font-bold text-rose-700">KIYANA</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">by Cirebon Cloth</div>
            </div>
            <div className="text-xs text-slate-500 text-center mb-1">Struk #{receipt.code}</div>
            <div className="text-[10px] text-slate-400 text-center mb-3">Petugas: {receipt.attendant_name || receipt.cashier_name}</div>
            <div className="text-sm space-y-1 border-y border-dashed border-slate-300 py-3">
              {receipt.items.map((it, i) => (
                <div key={i} className="flex justify-between"><span>{it.name} x{it.qty}</span><span>{formatIDR(it.subtotal)}</span></div>
              ))}
            </div>
            <div className="pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Total</span><span className="font-bold text-rose-700">{formatIDR(receipt.total)}</span></div>
              <div className="flex justify-between text-xs text-slate-500"><span>Metode</span><span>{receipt.payment_method === "mixed" ? `Campuran (Tunai ${formatIDR(receipt.cash_amount)} + ${receipt.mixed_method})` : receipt.payment_method}</span></div>
              {receipt.change > 0 && <div className="flex justify-between text-xs"><span>Kembali</span><span>{formatIDR(receipt.change)}</span></div>}
            </div>
            <button data-testid="pos-close-receipt" onClick={() => setReceipt(null)} className="mt-4 w-full h-11 rounded-xl bg-rose-600 text-white font-semibold">Selesai</button>
          </div>
        </div>
      )}

      {/* Layar terkunci / login petugas */}
      {(locked || (shift && !attendant)) && !receipt && (
        <LockScreen
          title={locked ? "KIYANA POS – Kasir Terkunci" : "KIYANA POS – Login Petugas"}
          desc="Scan ID Card atau masukkan PIN karyawan untuk melanjutkan."
          onUnlock={unlock}
        />
      )}
    </div>
  );
}
